import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Standalone Self-Audit Login — branded dynamically per tenant config.
 * Lives at /audit/login
 */
const AuditLogin = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { config, fetchPublicConfig } = useTenantConfig();
  const [email, setEmail] = useState(() => localStorage.getItem('audit_remember_email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('audit_remember_email'));

  useEffect(() => {
    if (user && user.role) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Try to load tenant config from last known tenant_id (stored in localStorage)
  useEffect(() => {
    const stored = localStorage.getItem('audit_tenant_id');
    if (stored) {
      fetchPublicConfig(stored);
    }
  }, [fetchPublicConfig]);

  // Check whether Microsoft SSO is configured, and surface any error the SSO redirect sent back
  useEffect(() => {
    fetch(`${API}/api/auth/sso-config`)
      .then(res => res.ok ? res.json() : { azure_enabled: false })
      .then(d => setSsoEnabled(!!d.azure_enabled))
      .catch(() => setSsoEnabled(false));

    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (ssoError === 'no_account') {
      setError('No account found for your Microsoft email. Ask your admin to invite you first.');
    } else if (ssoError === 'no_email') {
      setError('Your Microsoft account did not share an email address. Contact your admin.');
    } else if (ssoError === 'auth_failed') {
      setError('Microsoft sign-in failed. Please try again.');
    }
  }, []);

  const handleMicrosoftLogin = () => {
    window.location.href = `${API}/api/auth/azure/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password, rememberMe);
      // Store tenant_id so next time login page shows their branding
      if (data?.tenant_id) {
        localStorage.setItem('audit_tenant_id', data.tenant_id);
      }
      if (rememberMe) {
        localStorage.setItem('audit_remember_email', email);
      } else {
        localStorage.removeItem('audit_remember_email');
      }
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const c = config;

  return (
    <div className="min-h-screen w-full" style={{ fontFamily: "'Source Sans 3', sans-serif", backgroundColor: '#F7F9FC' }}>
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div className="grid min-h-screen lg:grid-cols-2">

        {/* Left decorative panel */}
        <div className="relative hidden lg:flex flex-col justify-center overflow-hidden" style={{ padding: '54px 60px', color: '#fff', background: 'linear-gradient(155deg, #24374f 0%, #1D2F47 52%, #16233a 100%)' }} data-testid="audit-login-hero">
          <svg viewBox="0 0 400 400" fill="none" aria-hidden="true" style={{ position: 'absolute', top: '-60px', right: '-80px', width: '520px', height: '520px', opacity: 0.9 }}>
            <g transform="rotate(-18 200 200)" strokeLinecap="round">
              <circle cx="200" cy="200" r="188" stroke="rgba(255,255,255,0.16)" strokeWidth="2" strokeDasharray="3 17" />
              <circle cx="200" cy="200" r="164" stroke="rgba(162,29,35,0.55)" strokeWidth="7" strokeDasharray="120 400" />
              <circle cx="200" cy="200" r="164" stroke="rgba(255,255,255,0.12)" strokeWidth="2" strokeDasharray="30 26" />
              <circle cx="200" cy="200" r="139" stroke="rgba(255,255,255,0.3)" strokeWidth="6" strokeDasharray="66 300" />
              <circle cx="200" cy="200" r="139" stroke="rgba(255,255,255,0.1)" strokeWidth="2" strokeDasharray="4 22" />
              <circle cx="200" cy="200" r="114" stroke="rgba(162,29,35,0.42)" strokeWidth="3" strokeDasharray="9 18" />
              <circle cx="200" cy="200" r="90" stroke="rgba(255,255,255,0.24)" strokeWidth="8" strokeDasharray="96 340" />
              <circle cx="200" cy="200" r="66" stroke="rgba(255,255,255,0.14)" strokeWidth="2" strokeDasharray="24 20" />
              <circle cx="200" cy="200" r="44" stroke="rgba(162,29,35,0.5)" strokeWidth="5" strokeDasharray="40 200" />
              <circle cx="200" cy="200" r="22" stroke="rgba(255,255,255,0.2)" strokeWidth="3" strokeDasharray="3 12" />
            </g>
          </svg>
          <svg viewBox="0 0 200 200" fill="none" aria-hidden="true" style={{ position: 'absolute', bottom: '-40px', left: '-50px', width: '260px', height: '260px', opacity: 0.85 }}>
            <g transform="rotate(24 100 100)" strokeLinecap="round">
              <circle cx="100" cy="100" r="94" stroke="rgba(255,255,255,0.12)" strokeWidth="2" strokeDasharray="3 15" />
              <circle cx="100" cy="100" r="72" stroke="rgba(255,255,255,0.2)" strokeWidth="5" strokeDasharray="52 200" />
              <circle cx="100" cy="100" r="52" stroke="rgba(162,29,35,0.45)" strokeWidth="3" strokeDasharray="8 15" />
              <circle cx="100" cy="100" r="30" stroke="rgba(255,255,255,0.16)" strokeWidth="4" strokeDasharray="30 120" />
            </g>
          </svg>
          <div style={{ position: 'absolute', top: '20%', left: '10%', width: '340px', height: '340px', background: 'radial-gradient(circle, rgba(162,29,35,0.25) 0%, rgba(162,29,35,0) 70%)', filter: 'blur(10px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', maxWidth: '560px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '22px 26px' }}>
            {c.logo_url && <img src={c.logo_url} alt={c.brand_name} style={{ height: 'clamp(56px, 5.9vw, 84px)', width: 'auto', flexShrink: 0 }} data-testid="audit-login-hero-logo" />}
            <div>
              <h1 style={{ fontFamily: 'Jost, sans-serif', fontSize: 'clamp(24px, 2.7vw, 36px)', fontWeight: 600, lineHeight: 1.05, margin: 0 }}>Communication<br />Audit</h1>
              <div style={{ height: '3px', width: '38px', background: '#A21D23', margin: '14px 0 0' }} />
            </div>
          </div>

          <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: '30px 44px', margin: '34px 0 0', padding: '22px 0 0', borderTop: '1px solid rgba(255,255,255,0.16)', maxWidth: '560px' }}>
            <div>
              <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>Access</div>
              <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '17px', fontWeight: 600, marginTop: '5px' }}>By invitation only</div>
            </div>
            <div>
              <div style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)' }}>Managed by</div>
              <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '17px', fontWeight: 600, marginTop: '5px' }}>Quality Assurance</div>
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex items-center justify-center" style={{ padding: '54px 32px', background: '#F7F9FC' }}>
          <div style={{ width: '100%', maxWidth: '404px' }}>
            {/* Mobile brand */}
            <div className="lg:hidden flex items-center gap-3 mb-7">
              {c.logo_url && <img src={c.logo_url} alt={c.brand_name} style={{ height: '48px', width: 'auto' }} />}
              <div>
                <div style={{ fontFamily: 'Jost, sans-serif', fontSize: '20px', fontWeight: 600, color: '#1D2F47', lineHeight: 1.1 }}>Communication Audit</div>
                <div style={{ height: '3px', width: '30px', background: '#A21D23', marginTop: '8px' }} />
              </div>
            </div>

            <h2 style={{ fontFamily: 'Jost, sans-serif', fontSize: '30px', fontWeight: 600, color: '#1D2F47', margin: 0, lineHeight: 1.1 }} data-testid="audit-login-heading">Sign in</h2>
            <p style={{ fontSize: '14.5px', color: '#5B6572', margin: '9px 0 0' }}>Use your Anderson College account.</p>

            {error && (
              <div className="rounded-md px-4 py-3" style={{ marginTop: '20px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#A21D23', fontSize: '13.5px' }} data-testid="audit-login-error">
                {error}
              </div>
            )}

            {ssoEnabled && (
              <>
                <button type="button" onClick={handleMicrosoftLogin} className="transition-opacity hover:opacity-90" style={{ width: '100%', margin: '22px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '11px', background: '#1D426C', color: '#fff', border: 'none', borderRadius: '5px', padding: '14px 16px', fontSize: '14.5px', fontWeight: 700, letterSpacing: '0.02em', cursor: 'pointer', boxShadow: '0 2px 8px rgba(29,66,108,0.22)' }} data-testid="audit-login-microsoft">
                  <svg width="16" height="16" viewBox="0 0 21 21" style={{ flexShrink: 0 }}><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
                  Continue with Microsoft
                </button>
                <p style={{ fontSize: '12.5px', color: '#5B6572', margin: '10px 0 0', textAlign: 'center' }}>Single sign-on — for staff who have been granted access</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', margin: '24px 0' }}>
                  <span style={{ flex: 1, height: '1px', background: '#E4E8EE' }} />
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#A6AEB9' }}>or</span>
                  <span style={{ flex: 1, height: '1px', background: '#E4E8EE' }} />
                </div>
              </>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ background: '#fff', border: '1px solid #E4E8EE', borderRadius: '6px', padding: '22px 22px 24px', boxShadow: '0 1px 3px rgba(29,47,71,0.05)' }} data-testid="audit-login-form">
                <label htmlFor="ca-email" style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5B6572' }}>Email</label>
                <input id="ca-email" required type="email" name="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full outline-none"
                  style={{ boxSizing: 'border-box', width: '100%', margin: '7px 0 0', fontSize: '14.5px', color: '#2F2F2F', padding: '11px 13px', border: '1px solid #DDE3EC', borderRadius: '5px', background: '#F7F9FC', transition: 'border-color 140ms, box-shadow 140ms' }}
                  onFocus={e => { e.target.style.borderColor = '#1D426C'; e.target.style.boxShadow = '0 0 0 3px rgba(29,66,108,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#DDE3EC'; e.target.style.boxShadow = 'none'; }}
                  placeholder={c.email_placeholder || 'you@andersoncollege.com'} data-testid="audit-login-email" />

                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', margin: '18px 0 0' }}>
                  <label htmlFor="ca-pass" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5B6572' }}>Password</label>
                </div>
                <div style={{ position: 'relative', margin: '7px 0 0' }}>
                  <input id="ca-pass" required type={showPw ? 'text' : 'password'} name="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full outline-none"
                    style={{ boxSizing: 'border-box', width: '100%', fontSize: '14.5px', color: '#2F2F2F', padding: '11px 46px 11px 13px', border: '1px solid #DDE3EC', borderRadius: '5px', background: '#F7F9FC', transition: 'border-color 140ms, box-shadow 140ms' }}
                    onFocus={e => { e.target.style.borderColor = '#1D426C'; e.target.style.boxShadow = '0 0 0 3px rgba(29,66,108,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#DDE3EC'; e.target.style.boxShadow = 'none'; }}
                    placeholder="Enter your password" data-testid="audit-login-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', top: '50%', right: '6px', transform: 'translateY(-50%)', background: 'transparent', border: 'none', padding: '6px 8px', fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5B6572', cursor: 'pointer' }} data-testid="audit-login-toggle-pw">{showPw ? 'Hide' : 'Show'}</button>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '9px', margin: '18px 0 0', fontSize: '13.5px', color: '#5B6572', cursor: 'pointer' }}>
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} style={{ width: '15px', height: '15px', accentColor: '#1D426C', cursor: 'pointer' }} data-testid="audit-login-remember" />
                  <span>Keep me signed in for 30 days</span>
                </label>

                <button type="submit" disabled={loading} className="disabled:opacity-50" style={{ width: '100%', margin: '20px 0 0', background: '#A21D23', color: '#fff', border: 'none', borderRadius: '5px', padding: '14px 16px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: loading ? 'wait' : 'pointer' }} data-testid="audit-login-submit">
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </div>
            </form>

            <p style={{ fontSize: '12.5px', lineHeight: 1.5, color: '#5B6572', margin: '22px 0 0' }}>Access is granted by Quality Assurance. No account? <a href="mailto:sampath.shenoy@andersoncollege.com" style={{ fontWeight: 600, textDecoration: 'none', color: '#1D426C' }} data-testid="audit-login-request-access">Request portal access</a></p>
            <p style={{ fontSize: '12px', color: '#5B6572', margin: '14px 0 0' }}>{c.footer_text || 'Anderson College Quality Assurance'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogin;
